import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff, LogIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'https://plansync-backend-234288438538.us-central1.run.app';

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: username,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Navigate to the MFA verification page
      navigate('/mfa-verification', { state: { username } });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // function handleTotpVerification(totpCode) {
  //   fetch('/api/auth/verify-totp', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({
  //       email: username,
  //       totp_code: totpCode,
  //     }),
  //   })
  //   .then(response => response.json())
  //   .then(data => {
  //     if (data.access_token) {
  //       // Store the access token and userid
  //       localStorage.setItem('access_token', data.access_token);
  //       localStorage.setItem('userid', data.user_data.userid);
  //       // Redirect to the dashboard or home page
  //       window.location.href = '/dashboard';
  //     } else {
  //       // Handle error
  //       console.error('TOTP verification failed');
  //     }
  //   })
  //   .catch(error => console.error('Error:', error));
  // }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-lg shadow-gray-400/20 p-8 w-full max-w-md border border-slate-200/50"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent mb-2">PlanSync Teams</h1>
          <p className="text-gray-500 text-sm">Sign in to your PlanSync AI account</p>
        </motion.div>
        
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 rounded-xl flex items-start space-x-2"
              role="alert"
            >
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <motion.div
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="relative group"
          >
            <Mail className="h-5 w-5 text-gray-400 absolute top-3.5 left-3 transition-colors group-hover:text-sky-500" />
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Email address"
              className="w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
              required
            />
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="relative group"
          >
            <Lock className="h-5 w-5 text-gray-400 absolute top-3.5 left-3 transition-colors group-hover:text-sky-500" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </motion.div>
          <div className="pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className={`w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-3.5 rounded-xl flex items-center justify-center space-x-2 font-medium shadow-lg shadow-sky-500/25 transition-all duration-200 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:shadow-sky-500/30'
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                  />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </motion.button>
          </div>
        </form>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mt-8 text-center pt-6 border-t border-gray-100"
        >
          <p className="text-gray-500 text-sm mb-2">Don't have an account?</p>
          <Link 
            to="/sign-up" 
            className="inline-flex items-center space-x-1 text-sky-600 hover:text-sky-700 font-medium transition-colors duration-200 group"
          >
            <span>Create account</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-sky-400/20 to-blue-600/20 rounded-full blur-2xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-blue-400/20 to-sky-600/20 rounded-full blur-2xl"
        />
      </motion.div>
    </div>
  );
};

export default LoginForm;

